<template>
  <article
    :id="item.id"
    :class="[tabId ? tabId + '-item' : '', 'item pt-7 pb-9']"
    :data-collections="item.collections"
  >
    <header
      class="mb-12 grid grid-cols-[225px] sm:grid-cols-[225px_225px] lg:grid-cols-[225px] xl:grid-cols-[225px_1fr] lg:text-center xl:text-left justify-center items-center gap-7"
    >
      <figure class="relative">
        <div
          v-if="item.itemStatus"
          class="font-bold absolute top-0 w-full text-white uppercase status bg-bc-le-gray text-[11px] text-center p-2 font-ubuntu"
        >
          {{ item.itemStatus }}
        </div>
        <a :href="item.link">
          <figure class="relative w-full">
            <img
              v-bind="getResponsiveImageAttrs(item.thumbnailImage)"
              class="w-[225px] h-[150px] object-cover"
            />
            <span
              v-if="item.productStatus?.statusMessage?.length"
              class="absolute left-0 px-4 py-[3px] text-white text-sm uppercase top-0 font-ubuntu bg-snipe-blue"
            >
              {{
                item.productStatus?.statusMessage[1] === "Custom Message"
                  ? item.productStatus?.statusCustom
                  : item.productStatus?.statusMessage[1]
              }}
            </span>
          </figure>
        </a>
      </figure>
      <div class="flex flex-col item-text gap-1">
        <p
          v-if="item.neighborhoodTitle"
          class="font-semibold leading-none text-black font-archer"
        >
          {{ item.neighborhoodTitle }}
        </p>
        <a class="title" :href="item.link">
          <h4
            class="font-ubuntu leading-tight font-bold text-[21px] text-bc-dark-blue whitespace-pre-line"
          >
            {{ item.title }}
          </h4>
        </a>
        <p class="text-base text-gray-500 details font-arial">
          <span
            v-for="subtitle in item.subtitle"
            :class="[
              tabId === 'quick-move-in' && subtitle.includes('$')
                ? 'font-bold text-black'
                : '',
              'block',
            ]"
          >
            {{ subtitle }}
          </span>
        </p>

        <AtomsCollectionButton
          v-for="collection in item.collections"
          :collection="collection"
          disableShadow
        />
      </div>
    </header>
    <footer class="text-center body">
      <a
        class="explore-button text-center py-[6px] inline-block text-[15px] font-medium font-ubuntu button-blue shadow rounded-md leading-none sm:leading-normal w-[190px] sm:w-[280px] max-w-full"
        :href="item.link"
      >
        {{ item.linkText }}
      </a>
    </footer>
  </article>
</template>

<style>
.item {
  .item-text {
    @apply items-start;
  }

  .atom-collection-button {
    @apply mb-0 p-0 block mr-auto;

    img {
      @apply w-7 h-7;
    }
    span {
      @apply text-lg;
    }
  }
}
</style>

<script setup>
const props = defineProps({
  itemRawData: Object,
  tabId: String,
  plans: Array,
});

const getResponsiveImageAttrs = (image) => useResponsiveImage(image, 'medium');

// Map data from query to standard data structure for use in template
const item = {};
switch (props.itemRawData.__typename) {
  case "Neighborhood":
    item.id = props.itemRawData.slug;
    item.title = props.itemRawData.title;
    item.link = props.itemRawData.uri;
    item.linkText = "EXPLORE NEIGHBORHOOD";
    item.itemStatus = getNeighborhoodStatusLabel(
      props.itemRawData.neighborhood.status
    );
    item.thumbnailSrc = getImageSrc(
      props.itemRawData.neighborhood.thumbnail,
      "medium"
    );
    item.thumbnailImage = props.itemRawData.neighborhood.thumbnail;
    item.thumbnailAlt = props.itemRawData.neighborhood.thumbnail.altText;

    item.subtitle = [
      getNeighborhoodNavSubtitle(
        props.itemRawData.types,
        props.itemRawData.basePrice > 5000 ? getRoundedPrice(props.itemRawData.basePrice) : null,
        props.itemRawData.neighborhood.status,
        true
      ),
    ];
    item.collections = getCollectionsByNeighborhood(
      props.itemRawData.slug,
      props.plans,
      null,
      null,
      [props.itemRawData.collection?.collection]
    );
    break;
  case "Model":
    item.id = props.itemRawData.slug;
    item.title = props.itemRawData.title;
    item.neighborhoodTitle = props.itemRawData.modelDetails.neighborhood.title;
    item.link = props.itemRawData.uri;
    item.linkText = "EXPLORE MODEL HOME";
    item.productStatus = {
      statusCustom: props.itemRawData?.productStatus?.statusCustom,
      statusMessage: props.itemRawData?.productStatus?.statusMessage,
    };
    item.thumbnailSrc = getImageSrc(
      props?.itemRawData?.featuredImage?.node ??
        props.itemRawData.modelDetails.neighborhood.neighborhood.thumbnail,
      "medium"
    );
    item.thumbnailImage = props?.itemRawData?.featuredImage?.node ??
        props.itemRawData.modelDetails.neighborhood.neighborhood.thumbnail;
    item.thumbnailAlt =
      props?.itemRawData?.featuredImage?.node.altText ??
      props.itemRawData.modelDetails.neighborhood.neighborhood.thumbnail
        .altText;
    item.subtitle = [
      getNeighborhoodNavSubtitle(
        props.itemRawData.modelDetails.neighborhood.types,
        props.itemRawData.modelDetails.neighborhood.basePrice > 5000 ? getRoundedPrice(props.itemRawData.modelDetails.neighborhood.basePrice) : null,
        props.itemRawData.modelDetails.neighborhood.neighborhood.status
      ),
    ];
    item.collections = [
      props?.itemRawData?.collection?.collection ??
        props?.itemRawData?.modelDetails?.floorplan?.floorplanDetails
          ?.collection,
    ];
    break;
  case "Home":
    item.id = props.itemRawData.slug;

    // Build custom title: [Address], [City]:\n[Floorplan Title]
    const address = props.itemRawData.qmiHomeDetails?.address1 || "";
    const city = props.itemRawData.qmiHomeDetails?.city || "";
    const floorplanTitle = props.itemRawData.qmiHomeDetails?.floorplan?.title || "";

    item.title = address && city && floorplanTitle
      ? `${address}, ${city}:\n${floorplanTitle}`
      : props.itemRawData.title;

    item.neighborhoodTitle =
      props.itemRawData.qmiHomeDetails.neighborhood.title;
    item.link = props.itemRawData.uri;
    item.linkText = "EXPLORE HOME DETAILS";
    item.productStatus = {
      statusCustom: props.itemRawData?.productStatus?.statusCustom,
      statusMessage: props.itemRawData?.productStatus?.statusMessage,
    };
    item.thumbnailSrc = getImageSrc(
      props?.itemRawData?.featuredImage?.node ??
        props.itemRawData.qmiHomeDetails.neighborhood.neighborhood.thumbnail,
      "medium"
    );
    item.thumbnailImage = props?.itemRawData?.featuredImage?.node ??
        props.itemRawData.qmiHomeDetails.neighborhood.neighborhood.thumbnail;
    item.thumbnailAlt =
      props?.itemRawData?.featuredImage?.node.altText ??
      props?.itemRawData?.qmiHomeDetails?.neighborhood?.neighborhood?.thumbnail
        ?.altText;
    item.subtitle = [
      props?.itemRawData?.qmiHomeDetails?.type,
      props?.itemRawData?.qmiHomeDetails?.bedrooms +
        " bed, " +
        props?.itemRawData?.qmiHomeDetails?.bathrooms +
        " bath",
      props?.itemRawData?.qmiHomeDetails?.finishedSqrFt.toLocaleString(
        "en-US"
      ) + " sq. ft",
      "$" + props?.itemRawData?.qmiHomeDetails?.price.toLocaleString("en-US"),
    ];
    item.collections = [
      props?.itemRawData?.collection?.collection ??
        props?.itemRawData?.qmiHomeDetails?.floorplan?.floorplanDetails
          ?.collection,
    ];
    break;
}
</script>
